import { useMemo, useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import { roleNeedsWarehouse } from '@eombor/contracts';
import type { Scope, ScopeType } from '@eombor/contracts';
import { cn } from '@eombor/ui';
import { t } from '../../../shared/i18n/index.js';
import { Combobox } from '../../../shared/ui/Combobox.js';
import { Select } from '../../../shared/ui/Select.js';

/** `GLOBAL` dan boshqa har bir doira aniq obyektga tayanadi. */
export type ScopedType = Exclude<ScopeType, 'GLOBAL'>;

/**
 * EKRANDAGI toifa. `STATION` shartnomada YO'Q — stansiya ham `ORG_UNIT`.
 * Lekin odam uchun korxona bilan stansiya ikki xil narsa va ular bitta
 * ro'yxatda aralashib turganda kerakligini topib bo'lmaydi: bitta MTU da
 * 75 tagacha stansiya bor.
 */
export type ScopeKind = 'MTU' | 'ORG_UNIT' | 'STATION' | 'WAREHOUSE';

/** Ekran toifasini shartnomadagi doira turiga o'giradi. */
export const scopeTypeOf = (kind: ScopeKind): ScopedType =>
  kind === 'STATION' ? 'ORG_UNIT' : kind;

export interface ScopeOption {
  id: string;
  label: string;
  kind: ScopeKind;
  /**
   * Obyekt qaysi MTU ichida. Ro'yxat shu bo'yicha qisqaradi — MTU tanlanmasa
   * obyekt ham tanlanmaydi.
   */
  mtuId: string | null;
  /** "PCH-12", "733104" — nom yonidagi rasmiy belgi. */
  hint?: string | null;
}

export const SCOPE_TYPES: readonly ScopeType[] = ['GLOBAL', 'MTU', 'ORG_UNIT', 'WAREHOUSE'];

/** Doira turining o'zbekcha nomi — `rbac.scopeGLOBAL` kabi kalitlar bilan. */
export const scopeTypeLabel = (type: ScopeType): string => t(`rbac.scope${type}`);

/** Ekran toifasining nomi — "Korxona" va "Stansiya" alohida ko'rinadi. */
export const scopeKindLabel = (kind: ScopeKind): string => t(`rbac.kind${kind}`);

/** Tanlash tartibi: xavfsizdan xavfliga emas, ish oqimi bo'yicha. */
const KINDS: readonly ScopeKind[] = ['MTU', 'ORG_UNIT', 'STATION', 'WAREHOUSE'];

/**
 * Rol o'zgarganda doirani unga MOSLAYDI.
 *
 * Ombor roli (`stock.receive` va shu kabilar) aniq bitta omborga beriladi —
 * sabab `WAREHOUSE_SCOPED_PERMISSIONS` yonida yozilgan. Bu yerda u faqat
 * TANLOVNI to'g'rilaydi; haqiqiy chegara backendda (`assertScopeForRole`),
 * chunki ekran qoidasi API ni himoya qilmaydi.
 *
 * `GLOBAL` tegilmaydi: u superadmin uchun ONGLI tanlov va ekranda qizil
 * ogohlantirish bilan keladi.
 */
export const scopeForRole = (permissions: readonly string[], current: Scope): Scope =>
  roleNeedsWarehouse(permissions) && current.type !== 'WAREHOUSE' && current.type !== 'GLOBAL'
    ? { type: 'WAREHOUSE', id: null }
    : current;

export interface ScopePickerProps {
  value: Scope;
  onChange: (next: Scope) => void;
  options: ScopeOption[];
  disabled?: boolean;
  /**
   * Berilsa — toifa ro'yxati SHU bittaga qisqaradi (`GLOBAL` dan tashqari).
   *
   * Ro'yxatni qisqartirish "tanlab, keyin xato ko'rish" dan yaxshiroq: odam
   * mavjud bo'lmagan yo'lni umuman ko'rmaydi. Nega qisqargani yozib ham
   * qo'yiladi, aks holda "Korxona qani?" degan savol tug'iladi.
   */
  requiredKind?: ScopeKind;
}

/**
 * Doira tanlagich.
 *
 * KMO da rollar global edi: xodimni bitta korxonadan chiqarish uning boshqa
 * hamma korxonalardagi rolini ham o'chirib yuborardi. Shuning uchun bu yerda
 * doira **majburiy** va u har doim ko'rinib turadi.
 *
 * Tanlov MTU DAN boshlanadi va bu tasodifiy emas: ruxsatning haqiqiy
 * chegarasi — MTU tuzilmasi, korxona ham, stansiya ham o'sha daraxt ichida
 * yashaydi (`org_unit.mtuId`). MTU siz butun tuzilma — 500 dan ortiq birlik
 * — bitta ro'yxatga tushardi va odam "Buxoro..." bilan "Qarshi..." orasidan
 * to'g'risini topolmasdi.
 */
export const ScopePicker = ({
  value,
  onChange,
  options,
  disabled = false,
  requiredKind,
}: ScopePickerProps) => {
  /*
   * MTU va toifa `value` DAN chiqariladi, alohida saqlanmaydi: tashqaridan
   * kelgan doira (masalan, xodimning o'z korxonasi) o'sha zahoti to'g'ri
   * MTU va toifani ko'rsatishi kerak. Qoralama holat faqat obyekt hali
   * tanlanmagan paytda kerak bo'ladi.
   */
  const selected = useMemo(
    () =>
      value.id === null
        ? null
        : (options.find(
            (option) => option.id === value.id && scopeTypeOf(option.kind) === value.type,
          ) ?? null),
    [options, value],
  );

  const [draftKind, setDraftKind] = useState<ScopeKind>('ORG_UNIT');
  const [draftMtuId, setDraftMtuId] = useState<string | null>(null);

  const global = value.type === 'GLOBAL';
  const kinds = requiredKind === undefined ? KINDS : [requiredKind];
  const kind: ScopeKind =
    requiredKind !== undefined && value.type !== 'GLOBAL'
      ? requiredKind
      : value.type === 'MTU'
        ? 'MTU'
        : (selected?.kind ?? draftKind);
  const mtuId: string | null =
    value.type === 'MTU' ? value.id : (selected?.mtuId ?? draftMtuId);

  const mtus = useMemo(
    () =>
      options
        .filter((option) => option.kind === 'MTU')
        .sort((a, b) => (a.hint ?? a.label).localeCompare(b.hint ?? b.label)),
    [options],
  );

  /* Obyekt ro'yxati IKKI shart bilan qisqaradi: tanlangan MTU va toifa. */
  const objects = useMemo(
    () =>
      mtuId === null
        ? []
        : options.filter((option) => option.kind === kind && option.mtuId === mtuId),
    [options, kind, mtuId],
  );

  const setKind = (next: ScopeKind): void => {
    setDraftKind(next);
    // Toifa o'zgardi — eski obyekt endi boshqa turdagi narsani bildiradi.
    // MTU esa saqlanadi: odam odatda o'sha MTU ichida qidirishda davom etadi.
    onChange(next === 'MTU' ? { type: 'MTU', id: mtuId } : { type: scopeTypeOf(next), id: null });
  };

  const setMtu = (next: string | null): void => {
    setDraftMtuId(next);
    onChange(kind === 'MTU' ? { type: 'MTU', id: next } : { type: scopeTypeOf(kind), id: null });
  };

  return (
    <div className="flex flex-col gap-3">
      <Select
        label={t('rbac.scopeType')}
        value={global ? 'GLOBAL' : kind}
        disabled={disabled}
        options={[
          ...kinds.map((item) => ({ value: item, label: scopeKindLabel(item) })),
          { value: 'GLOBAL', label: scopeTypeLabel('GLOBAL') },
        ]}
        onChange={(event) => {
          const next = event.target.value;
          if (next === 'GLOBAL') onChange({ type: 'GLOBAL', id: null });
          else setKind(next as ScopeKind);
        }}
      />

      {requiredKind === undefined || global ? null : (
        <p className="text-text-muted text-xs text-pretty">{t('rbac.scopeRequiredWarehouse')}</p>
      )}

      {global ? (
        <p
          role="alert"
          className={cn(
            'border-warn/25 flex items-start gap-2 rounded-[var(--radius-sm)] border',
            'bg-warn-soft text-warn px-3 py-2 text-sm',
            'shadow-[var(--shadow-rail-warn)]',
          )}
        >
          <AlertTriangle size={16} aria-hidden className="mt-0.5 shrink-0" />
          {t('rbac.scopeGlobalWarning')}
        </p>
      ) : (
        <>
          {/* MTU har doim va BIRINCHI: ruxsatning chegarasi shu. */}
          <Combobox
            label={t('rbac.scopeMtu')}
            placeholder={t('rbac.scopeMtuPlaceholder')}
            emptyText={t('rbac.scopeNoOptions')}
            options={mtus.map((option) => ({
              value: option.id,
              label: option.label,
              hint: option.hint,
            }))}
            value={mtuId}
            onChange={setMtu}
            disabled={disabled}
          />

          {kind === 'MTU' ? null : (
            <Combobox
              label={scopeKindLabel(kind)}
              placeholder={
                mtuId === null ? t('rbac.scopeMtuFirst') : t('rbac.scopeObjectPlaceholder')
              }
              emptyText={t('rbac.scopeNoOptions')}
              options={objects.map((option) => ({
                value: option.id,
                label: option.label,
                hint: option.hint,
              }))}
              value={value.id}
              onChange={(next) => onChange({ type: scopeTypeOf(kind), id: next })}
              disabled={disabled || mtuId === null}
            />
          )}

          {value.id === null ? (
            <p className="text-warn text-xs">
              {mtuId === null ? t('rbac.scopeMtuFirst') : t('rbac.scopeObjectRequired')}
            </p>
          ) : null}
        </>
      )}
    </div>
  );
};
